import React, { useMemo } from 'react';
import { TASK_TIME_MAX_HOURS, TASK_TIME_MIN_HOURS, TASK_TIME_STEP } from '../../utils/taskTimeConstants';

/**
 * Half-hour picker capped at what the client actually has left this period.
 *
 * @param {number|null} maxHours  Hours still available. Omit for the hard 40h cap.
 *                                A `value` above the cap is kept as an option so
 *                                editing an existing task never silently drops it.
 */
function buildHourOptions(maxHours, value) {
  const cap = Number.isFinite(Number(maxHours))
    ? Math.min(TASK_TIME_MAX_HOURS, Number(maxHours))
    : TASK_TIME_MAX_HOURS;
  const options = [];
  for (let h = TASK_TIME_MIN_HOURS; h <= cap + 1e-9; h += TASK_TIME_STEP) {
    const rounded = Math.round(h * 2) / 2;
    options.push({ value: String(rounded), label: `${rounded}h` });
  }
  const current = Number(value);
  if (Number.isFinite(current) && current > 0 && !options.some((o) => Number(o.value) === current)) {
    options.push({ value: String(current), label: `${current}h (current)` });
    options.sort((a, b) => Number(a.value) - Number(b.value));
  }
  return options;
}

const TaskTimeHoursInput = ({
  value,
  onChange,
  id = 'task-time-hours',
  className = '',
  disabled = false,
  maxHours = null,
}) => {
  const options = useMemo(() => buildHourOptions(maxHours, value), [maxHours, value]);

  if (options.length === 0) {
    return (
      <p className={`text-sm text-red-600 dark:text-red-400 ${className}`}>
        No implementation hours remaining this period.
      </p>
    );
  }

  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white ${className}`}
    >
      <option value="">Select hours</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

export default TaskTimeHoursInput;
